'use client'

import { VoiceInputButton } from '@/src/components/common/voice/VoiceInputButton'
import type { HomeAIMessages } from '@/src/utils/homeAISearch/locale'
import { ArrowUpwardSharp } from '@mui/icons-material'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import { Box, CircularProgress, IconButton, InputAdornment, TextField, alpha } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'

type ResponsiveInputSize = number | { mobile: number; desktop: number }

interface AnimatedAISearchInputProps {
    isMobile: boolean
    showPlaceHolder: boolean
    messages: HomeAIMessages
    placeholder?: string
    placeholderHints?: string[]
    startIconSize?: number
    submitIconSize?: number
    loadingIndicatorSize?: number
    submitButtonSize?: ResponsiveInputSize
    inputValue: string
    loading: boolean
    locale: string
    onInputChange: (nextValue: string) => void
    onVoiceTranscript: (transcript: string) => void
    onSubmit: () => void
}

export const AnimatedAISearchInput = ({
    isMobile,
    showPlaceHolder,
    messages,
    placeholder,
    placeholderHints,
    startIconSize = 18,
    submitIconSize = 20,
    loadingIndicatorSize = 18,
    submitButtonSize = { mobile: 30, desktop: 42 },
    inputValue,
    loading,
    locale,
    onInputChange,
    onVoiceTranscript,
    onSubmit
}: AnimatedAISearchInputProps) => {
    const [placeholderIndex, setPlaceholderIndex] = useState(0)
    const [charIndex, setCharIndex] = useState(0)
    const [isDeleting, setIsDeleting] = useState(false)
    const isQuickReplyHintMode = Boolean(placeholderHints?.length)
    const fallbackPlaceholder = placeholder ?? messages.placeholder
    const resolvedSubmitButtonSize = typeof submitButtonSize === 'number'
        ? submitButtonSize
        : isMobile ? submitButtonSize.mobile : submitButtonSize.desktop

    const placeholderPool = useMemo(
        () => (
            placeholderHints?.length
                ? placeholderHints
                : placeholder
                    ? [placeholder]
                : messages.animatedPlaceholders.length
                    ? messages.animatedPlaceholders
                    : [fallbackPlaceholder]
        ),
        [fallbackPlaceholder, messages.animatedPlaceholders, placeholder, placeholderHints]
    )
    const currentPlaceholder = placeholderPool[placeholderIndex] ?? fallbackPlaceholder
    const animatedPlaceholder = currentPlaceholder.slice(0, charIndex)

    useEffect(() => {
        setPlaceholderIndex(0)
        setCharIndex(0)
        setIsDeleting(false)
    }, [fallbackPlaceholder, placeholderHints, showPlaceHolder])

    useEffect(() => {
        if (inputValue.trim() || !showPlaceHolder) return

        const typingSpeed = isQuickReplyHintMode ? (isDeleting ? 55 : 78) : (isDeleting ? 32 : 52)
        const holdAtEnd = isQuickReplyHintMode ? 2300 : 1100
        const holdBeforeNext = isQuickReplyHintMode ? 900 : 260

        const timeout = setTimeout(() => {
            if (!isDeleting && charIndex < currentPlaceholder.length) {
                setCharIndex(prev => prev + 1)
                return
            }

            if (!isDeleting && charIndex === currentPlaceholder.length) {
                setIsDeleting(true)
                return
            }

            if (isDeleting && charIndex > 0) {
                setCharIndex(prev => prev - 1)
                return
            }

            setIsDeleting(false)
            setPlaceholderIndex(prev => (prev + 1) % placeholderPool.length)
        }, !isDeleting && charIndex === currentPlaceholder.length
            ? holdAtEnd
            : isDeleting && charIndex === 0
                ? holdBeforeNext
                : typingSpeed)

        return () => clearTimeout(timeout)
    }, [charIndex, currentPlaceholder, inputValue, isDeleting, isQuickReplyHintMode, placeholderPool.length, showPlaceHolder])

    return (
        <TextField
            size={isMobile ? 'small' : 'medium'}
            fullWidth
            minRows={1}
            maxRows={2}
            value={inputValue}
            onChange={e => onInputChange(e.target.value)}
            onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    onSubmit()
                }
            }}
            placeholder={showPlaceHolder ? animatedPlaceholder : ' '}
            slotProps={{
                input: {
                    startAdornment: (
                        <InputAdornment position="start" >
                            <AutoAwesomeOutlinedIcon sx={{ fontSize: startIconSize, color: 'primary.main' }} />
                        </InputAdornment>
                    ),
                    endAdornment: (
                        <InputAdornment position="end" >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <VoiceInputButton
                                    locale={locale}
                                    disabled={loading}
                                    size="small"
                                    onTranscript={onVoiceTranscript}
                                />
                                <IconButton
                                    size="small"
                                    aria-label={messages.searchAriaLabel}
                                    onClick={() => onSubmit()}
                                    disabled={loading || !inputValue.trim()}
                                    sx={theme => ({
                                        width: resolvedSubmitButtonSize,
                                        height: resolvedSubmitButtonSize,
                                        bgcolor: 'primary.main',
                                        color: 'primary.contrastText',
                                        '&:hover': {
                                            bgcolor: theme.palette.primary.dark
                                        },
                                        '&.Mui-disabled': {
                                            bgcolor: alpha(theme.palette.primary.main, 0.35),
                                            color: alpha(theme.palette.primary.contrastText, 0.8)
                                        }
                                    })}
                                >
                                    {loading ? (
                                        <CircularProgress size={loadingIndicatorSize} color="inherit" />
                                    ) : (
                                        <ArrowUpwardSharp sx={{ fontSize: submitIconSize }} />
                                    )}
                                </IconButton>
                            </Box>
                        </InputAdornment>
                    )
                }
            }}
            sx={theme => ({
                '& .MuiOutlinedInput-root': {
                    borderRadius: 999,
                    pr: 1,
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                    transition: theme.transitions.create(['border-color', 'box-shadow'], {
                        duration: theme.transitions.duration.short
                    })
                },
                '& .MuiOutlinedInput-root.Mui-focused': {
                    boxShadow: [
                        `0 0 0 1px ${alpha(theme.palette.primary.main, 0.5)}`,
                        `-24px 0 34px -16px ${alpha(theme.palette.primary.main, 0.82)}`,
                        `24px 0 34px -16px ${alpha(theme.palette.secondary.main, 0.9)}`,
                        `0 10px 24px -18px ${alpha(theme.palette.primary.dark, 0.68)}`
                    ].join(', ')
                },
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.primary.main, 0.25)
                },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.primary.main, 0.4)
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 1.5,
                    borderColor: 'primary.main'
                },
                '& .MuiInputBase-inputMultiline': {
                    maxHeight: 112,
                    overflowY: 'auto',
                    pr: 0.5
                }
            })}
        />
    )
}
